import AddWalletBalanceMonitorDialogProvider, {
  useDialogProviderContext,
} from "src/features/DialogProvider";
import { useAlerting, useTranslation } from "src/hooks";
import Content from "./content";
import { useEffect, useState } from "react";
import { AddressBookFields } from "./content/components/WalletFieldSection/components/WalletAddressTextField";
import useValidation from "./hooks/useValidation";
import { usePageConfigProviderContext } from "src/features/PageConfigProvider";
import { addWalletBalanceMonitor } from "src/api/merchant";
import EnumCriteria from "@wallet-manager/node-types/dist/src/postgres/const/Criteria";
import { useChains } from "src/components/FetchConfig";
import {
  amountToCryptoAmount,
  findDecimalByChainNameAndAsset,
} from "src/utils/helper";
import { appError } from "src/utils/errorHandler";

export type AddWalletBalanceMonitorFields = {
  chainName: string;
  currency: string;
  addressBookFields: AddressBookFields;
  thresholdLower: string;
  thresholdUpper: string;
};

export const initAddressBookFields: AddressBookFields = {
  to_address: "",
  amount: "",
  display_name: "",
  wallet_type: "",
  to_wallet_tag: "",
};

const initFields: AddWalletBalanceMonitorFields = {
  chainName: "",
  currency: "",
  addressBookFields: initAddressBookFields,
  thresholdLower: "",
  thresholdUpper: "",
};

function AddWalletBalanceMonitorDialogDialog() {
  const [fields, setFields] =
    useState<AddWalletBalanceMonitorFields>(initFields);
  const { translatePrefix, refreshData } = usePageConfigProviderContext();
  const { t, tc } = useTranslation(translatePrefix);
  const { validate } = useValidation();

  const { isOpen, setIsOpen } = useDialogProviderContext();

  const { selectChainByPieces } = useChains();

  const { alerting } = useAlerting();

  const { chain_type, chain_id } = selectChainByPieces({
    chain_name: fields.chainName,
  });

  useEffect(() => {
    if (isOpen) {
      setFields(initFields);
    }
  }, [isOpen]);

  const onConfirm = async () => {
    const isValid = validate(fields);

    if (!isValid || chain_type === undefined || chain_id === undefined) {
      return;
    }

    const decimal = findDecimalByChainNameAndAsset(
      String(chain_type),
      chain_id,
      fields.currency,
    );

    const getDetails = () => {
      const lowerThresholdDetail = fields.thresholdLower
        ? [
            {
              criteria: EnumCriteria.LessThan,
              thresholdValue: amountToCryptoAmount(
                fields.thresholdLower,
                Number(decimal),
              ),
            },
          ]
        : [];
      const upperThresholdDetail = fields.thresholdUpper
        ? [
            {
              criteria: EnumCriteria.GreaterThan,
              thresholdValue: amountToCryptoAmount(
                fields.thresholdUpper,
                Number(decimal),
              ),
            },
          ]
        : [];

      return [...lowerThresholdDetail, ...upperThresholdDetail];
    };

    const res = await addWalletBalanceMonitor({
      assetName: fields.currency,
      walletAddress: fields.addressBookFields.to_address,
      details: getDetails(),
      chainType: chain_type,
      chainId: chain_id,
    });

    if (res.error?.code === -51) {
      alerting("warning", t("monitor_already_exists_warning"));
      return;
    }

    if (res.error && !res.result) {
      appError({ ...res.error, requestId: res.requestId });
      return;
    }

    alerting("success", t("add_wallet_balance_monitor_success"));
    refreshData();
    setIsOpen(false);
  };

  const isLowerThresholdInvalid =
    Number(fields.thresholdLower) < 0 || isNaN(Number(fields.thresholdLower));
  const isUpperThresholdInvalid =
    fields.thresholdUpper !== "" &&
    (Number(fields.thresholdUpper) <= 0 ||
      isNaN(Number(fields.thresholdUpper)));

  const isConfirmDisabled =
    (!fields.thresholdLower && !fields.thresholdUpper) ||
    isLowerThresholdInvalid ||
    isUpperThresholdInvalid;

  return (
    <AddWalletBalanceMonitorDialogProvider.DialogInOne
      title={t("add_wallet_balance_monitor_dialog_title")}
      content={
        <Content
          fields={fields}
          setFields={setFields}
          isLowerThresholdInvalid={isLowerThresholdInvalid}
          isUpperThresholdInvalid={isUpperThresholdInvalid}
        />
      }
      onConfirm={onConfirm}
      isConfirmDisabled={isConfirmDisabled}
      maxWidth="md"
    />
  );
}

export default AddWalletBalanceMonitorDialogDialog;
